import { Col, message, Spin } from "antd";
import Text from "antd/lib/typography/Text";
import Title from "antd/lib/typography/Title";
import { SearchOutlined } from "@ant-design/icons";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BUTTON_SIZE, INPUT_SIZE } from "../../../common/ThemeConstants";
import FormButton from "../../../components/general/FormButton";
import FormContainer from "../../../components/general/FormContainer";
import FormRadioButtons from "../../../components/general/FormRadioButtons";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import ReportExcelDownload from "../../../components/ReportingComponents/ReportExcelDownload";
import { getDate } from "../../../functions/dateFunctions";
import { postRequest } from "../../../services/mainApp.service";
import { formatPeriodForPrint } from "../callCenterReportPeriodFormat";
import OrderDetailReportTemp from "./OrderDetailReportTemp";

const TYPE_OPTIONS = [
  { id: 1, name: "Branch" },
  { id: 2, name: "Area" },
  { id: 3, name: "Agent" },
  { id: 4, name: "Customer" },
  { id: 5, name: "Product" },
  { id: 6, name: "Payment Term" },
  { id: 7, name: "Order Mode" },
];

const TYPE_MAP = {
  1: "Branch",
  2: "Area",
  3: "Agent",
  4: "Customer",
  5: "Product",
  6: "Payment Term",
  7: "Order Mode",
};

function inferSelectFields(sample) {
  if (!sample) return { idName: "Id", valueName: "Name" };
  const keys = Object.keys(sample);
  const idName =
    keys.find((k) => /Id$/i.test(k) && !/^CompanyId$/i.test(k)) ||
    keys.find((k) => /^Id$/i.test(k)) ||
    keys[0];
  const valueName =
    keys.find((k) => /Name$/i.test(k) && k !== idName) ||
    keys.find((k) => /Title|Label|Description$/i.test(k)) ||
    keys.find((k) => k !== idName);
  return { idName, valueName: valueName || "Name" };
}

function dataTypeFromTypeId(typeId) {
  if (typeId == null) return null;
  return TYPE_MAP[typeId] || null;
}

const pad2 = (n) => String(n).padStart(2, "0");

function businessDateToDateTimeLocalStart(dateStr) {
  const s = String(dateStr ?? "").trim();
  const parts = s.split(/[-T]/).slice(0, 3).map((x) => parseInt(x, 10));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T00:00`;
  }
  const [y, m, d] = parts;
  return `${y}-${pad2(m)}-${pad2(d)}T00:00`;
}

function businessDateToDateTimeLocalEnd(dateStr) {
  const s = String(dateStr ?? "").trim();
  const parts = s.split(/[-T]/).slice(0, 3).map((x) => parseInt(x, 10));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T23:59`;
  }
  const [y, m, d] = parts;
  return `${y}-${pad2(m)}-${pad2(d)}T23:59`;
}

function dateTimeLocalToBusinessDate(dtLocal) {
  const datePart = String(dtLocal ?? "").split("T")[0];
  const segs = datePart.split("-").map((x) => parseInt(x, 10));
  if (segs.length < 3 || segs.some((n) => Number.isNaN(n))) return getDate();
  const [y, m, d] = segs;
  return `${y}-${m}-${d}`;
}

function formatDateTimeForApi(dtLocal) {
  const raw = String(dtLocal ?? "").trim();
  if (!raw.includes("T")) return raw;
  const [date, time] = raw.split("T");
  const timePart =
    time && time.split(":").length === 2 ? `${time}:00` : time || "00:00:00";
  return `${date} ${timePart}`;
}

function buildReportDatePayload(data) {
  const dateFromVal =
    data.FetchReportBy === "OrderDate"
      ? formatDateTimeForApi(data.DateFrom)
      : data.DateFrom || getDate();
  const dateToVal =
    data.FetchReportBy === "OrderDate"
      ? formatDateTimeForApi(data.DateTo)
      : data.DateTo || getDate();
  const reportBy =
    data.FetchReportBy === "OrderDate" ? "OrderDate" : "BusinessDay";
  return { dateFromVal, dateToVal, reportBy };
}

const CallCenterOrderDetail = () => {
  const userData = useSelector((state) => state.authReducer);
  const [data, setData] = useState({
    DateFrom: getDate(),
    DateTo: getDate(),
    FetchReportBy: "BusinessDate",
    TypeId: null,
    ReferenceId: null,
    Search: "",
    ReportMode: "Detail",
  });
  const [dependentList, setDependentList] = useState([]);
  const [refSelectFields, setRefSelectFields] = useState({
    idName: "Id",
    valueName: "Name",
  });
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [list, setList] = useState([]);
  const [disableExcel, setDisableExcel] = useState(true);
  const [loading, setLoading] = useState(false);

  const filterType = useMemo(() => dataTypeFromTypeId(data.TypeId), [data.TypeId]);
  const isCustomer = filterType === "Customer";
  const isSummaryMode = data.ReportMode === "Summary";
  const showReferenceDropdown =
    filterType && !isCustomer && ["Branch", "Area", "Agent", "Payment Term", "Order Mode"].includes(filterType);

  const loadDependentOptions = useCallback(() => {

    const type = dataTypeFromTypeId(data.TypeId);
    if (!type || type === "Customer") {
      setDependentList([]);
      setRefSelectFields({ idName: "Id", valueName: "Name" });
      return;
    }

    const controller = new AbortController();
    setLoadingOptions(true);
    const { dateFromVal, dateToVal, reportBy } = buildReportDatePayload(data);
    postRequest(
      "/CallCenterOrderDetailSummary",
      {
        OperationId: 0,
        CompanyId: userData.CompanyId,
        DateFrom: dateFromVal,
        DateTo: dateToVal,
        ReportBy: reportBy,
        DataType: type,
        Id: null,
        Search: null,
      },
      controller
    )
      .then((res) => {
        setLoadingOptions(false);
        if (res.error) {
          message.error(res.errorMessage || "Failed to load list");
          setDependentList([]);
          return;
        }
        const rows = res?.data?.DataSet?.Table || [];
        setDependentList(rows);
        if (rows.length > 0) {
          setRefSelectFields(inferSelectFields(rows[0]));
        } else {
          setRefSelectFields({ idName: "Id", valueName: "Name" });
        }
      })
      .catch((err) => {
        setLoadingOptions(false);
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to load list");
        }
        setDependentList([]);
      });
    return () => controller.abort();
  }, [
    data.TypeId,
    data.DateFrom,
    data.DateTo,
    data.FetchReportBy,
    userData.CompanyId,
  ]);

  useEffect(() => {
    if (!showReferenceDropdown) {
      setDependentList([]);
      return undefined;
    }
    return loadDependentOptions();
  }, [showReferenceDropdown, loadDependentOptions]);

  const handleSearchChange = (e) => {

    setData((prev) => {
      const next = { ...prev, [e.name]: e.value };
      if (e.name === "TypeId") {
        next.ReferenceId = null;
        next.Search = "";
      }
      return next;
    });
    setList([]);
    setDisableExcel(true);
  };

  const handleFetchReportByChange = (e) => {
    const next = e.target.value;
    setData((prev) => {
      if (next === prev.FetchReportBy) return prev;
      if (next === "OrderDate") {
        return {
          ...prev,
          FetchReportBy: next,
          DateFrom: businessDateToDateTimeLocalStart(prev.DateFrom),
          DateTo: businessDateToDateTimeLocalEnd(prev.DateTo),
        };
      }
      return {
        ...prev,
        FetchReportBy: next,
        DateFrom: dateTimeLocalToBusinessDate(prev.DateFrom),
        DateTo: dateTimeLocalToBusinessDate(prev.DateTo),
      };
    });
    setList([]);
    setDisableExcel(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!data.TypeId) {
      message.error("Please select Type");
      return;
    }
    if (data.DateFrom && data.DateTo) {
      const fromT = new Date(data.DateFrom);
      const toT = new Date(data.DateTo);
      if (fromT > toT) {
        message.error("Date From must be less than or equal to Date To");
        return;
      }
    }

    const type = dataTypeFromTypeId(data.TypeId);
    if (!isCustomer) {
      if (!isSummaryMode && !data.ReferenceId && type !== "Product") {
        message.error(`Please select ${type}`);
        return;
      }
    } else {
      if (!(data.Search || "").trim()) {
        message.error("Please enter customer search text");
        return;
      }
    }

    setLoading(true);
    const { dateFromVal, dateToVal, reportBy } = buildReportDatePayload(data);
    postRequest("/CallCenterOrderDetailSummary", {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      DateFrom: dateFromVal,
      DateTo: dateToVal,
      ReportBy: reportBy,
      DataType: type,
      Id: isCustomer ? null : data.ReferenceId,
      Search: isCustomer ? (data.Search || "").trim() : null,
      DetailSummary: isSummaryMode ? "Summary" : "Detail",
    })
      .then((res) => {
        setLoading(false);
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch report");
          setList([]);
          setDisableExcel(true);
          return;
        }
        const reportData = res?.data?.DataSet?.Table || res?.data?.Data || [];
        if (!reportData.length) {
          message.error("No records found!");
          setList([]);
          setDisableExcel(true);
          return;
        }
        setList(reportData);
        setDisableExcel(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch report");
        }
        setList([]);
        setDisableExcel(true);
      });
  };

  const subtitleParts = [
    `Type: ${filterType || "—"}`,
    formatPeriodForPrint(data.FetchReportBy, data.DateFrom, data.DateTo),
    isCustomer
      ? `Search: ${(data.Search || "").trim()}`
      : filterType && data.ReferenceId,
  ].filter(Boolean);

  const searchPanel = (
    <Fragment>
      <FormTextField
        colSpan={5}
        label="Date From"
        type={data.FetchReportBy === "OrderDate" ? "datetime-local" : "date"}
        name="DateFrom"
        size={INPUT_SIZE}
        value={data.DateFrom}
        onChange={handleSearchChange}
      />
      <FormTextField
        colSpan={5}
        label="Date To"
        type={data.FetchReportBy === "OrderDate" ? "datetime-local" : "date"}
        name="DateTo"
        size={INPUT_SIZE}
        value={data.DateTo}
        onChange={handleSearchChange}
      />
      <Col span={5} className="selectInput rounded-md">
        <Text>Fetch Report By</Text>
        <select
          className="form-select border-gray-300 rounded-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-2 h-10 mt-1 block w-full"
          name="FetchReportBy"
          value={data.FetchReportBy}
          onChange={handleFetchReportByChange}
          style={{ width: "100%" }}
        >
          <option value="BusinessDate">BusinessDate</option>
          <option value="OrderDate">OrderDate</option>
        </select>
      </Col>
      <FormSelect
        colSpan={5}
        listItem={TYPE_OPTIONS}
        idName="id"
        valueName="name"
        size={INPUT_SIZE}
        name="TypeId"
        label="Report Type"
        value={data.TypeId}
        onChange={handleSearchChange}
        allowClear={true}
        required={true}
      />
      {showReferenceDropdown && (
        <FormSelect
          colSpan={6}
          listItem={dependentList || []}
          idName={refSelectFields.idName}
          valueName={refSelectFields.valueName}
          size={INPUT_SIZE}
          name="ReferenceId"
          label={filterType}
          value={data.ReferenceId}
          onChange={handleSearchChange}
          allowClear={true}
          required={!isSummaryMode}
          disabled={loadingOptions}
        />
      )}
      {isCustomer && (
        <FormTextField
          colSpan={6}
          label="Customer"
          type="text"
          name="Search"
          size={INPUT_SIZE}
          value={data.Search}
          onChange={handleSearchChange}
          placeholder="Search customer"
          required={true}
        />
      )}

      {!isCustomer && (
        <FormRadioButtons
          colSpan={6}
          name="ReportMode"
          value={data.ReportMode}
          onChange={handleSearchChange}
          list={[
            { value: "Detail", name: "Detail" },
            { value: "Summary", name: "Summary" },
          ]}
        />)}

      <FormButton
        title="Search"
        type="primary"
        size={BUTTON_SIZE}
        colSpan={2}
        htmlType="submit"
        icon={<SearchOutlined />}
      />
    </Fragment>
  );

  const showReport = !disableExcel;

  return (
    <div style={{ margin: 5 }}>
      <Title level={3} style={{ color: "#017971" }}>
        Call Center Order Detail
      </Title>
      <div
        style={{
          background: "white",
          padding: 15,
          boxShadow: "0 0 5px lightgray",
          borderRadius: 2,
        }}
      >
        <FormContainer
          rowStyle={{ alignItems: "flex-end" }}
          onSubmit={handleSearchSubmit}
        >
          <Fragment>
            {loadingOptions && (
              <span style={{ marginRight: 8, alignSelf: "center" }}>
                <Spin size="small" />
              </span>
            )}
            {searchPanel}
          </Fragment>
        </FormContainer>
        <div
          style={{ margin: "10px 0", borderTop: "1px solid lightgray" }}
        ></div>
      </div>

      <ReportExcelDownload
        fileName={`CallCenterOrderDetail_${data.DateFrom}_${data.DateTo}`}
      >
        {loading && (
          <Spin
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        )}
        {showReport && (
          <OrderDetailReportTemp
            list={list}
            headList={list.length > 0 ? Object.keys(list[0]) : []}
            subtitle={subtitleParts.join(" · ")}
          />
        )}
      </ReportExcelDownload>
    </div>
  );
};

export default CallCenterOrderDetail;
